// ──────────────────────────────────────────────────────────────
// QCARE-CHAT-MARKDOWN-LINKS (2026-05-12)
//
// Assistant text rendering helpers for the chat UI.
//   - `stripMarkdown(text)` — strips formatting artifacts (bold,
//     italic, code, headings, link syntax → label) and returns a
//     plain string. Used by the CLIPBOARD copy path and as the
//     pre-render normalization step for displayed text.
//   - `parseAssistantTextSegments(text)` — pure splitter that
//     scans for inline Markdown links `[label](https://…)` and
//     returns an array of `{ kind: 'text' | 'link', text?, label?,
//     href? }` segments. Unsafe URLs (non-http/https) fall through
//     as plain text. Testable via lib/assistant-text.spec.cjs.
//   - `renderAssistantText(text)` — React component that converts
//     the parsed segments into a `ReactNode` (text + `<a>` anchors).
//     Anchors open in a new tab with `rel="noopener noreferrer"`
//     and styled with the QAI cyan accent.
//
// PROD trigger (2026-05-12): the Training Hub branch row added
// Google Maps links in inline-Markdown form
//   `[افتح موقع فرع التحلية على الخريطة](https://maps.app.goo.gl/…)`
// The old `stripMarkdown` did not handle `[…](…)` syntax, so the
// raw markdown rendered as literal text in the chat bubbles.
// ──────────────────────────────────────────────────────────────
import React from 'react';

/** Inline Markdown link regex. Only matches http:// or https:// to
 *  reject `javascript:`, `data:`, `mailto:`, `tel:` etc. by
 *  construction. Label cannot contain `]` or a newline. */
const MD_LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

/** Strip markdown formatting artifacts from assistant text. Used
 *  for clipboard copy and as the pre-render step (where rendering
 *  goes through `renderAssistantText`, the link syntax is replaced
 *  with the label string + an anchor; for copy this strip just
 *  keeps the label and drops the URL). Pure / no I/O. */
export function stripMarkdown(text: string): string {
    if (!text) return '';
    return text
        // [label](https://url) → label (so clipboard copy reads the
        // label, not the markdown syntax or the raw URL).
        .replace(MD_LINK_RE, '$1')
        .replace(/^#{1,4}\s+/gm, '')     // ### headings
        .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
        .replace(/\*([^*]+)\*/g, '$1')     // *italic*
        .replace(/__([^_]+)__/g, '$1')     // __bold__
        .replace(/_([^_]+)_/g, '$1')       // _italic_
        .replace(/`([^`]+)`/g, '$1');      // `code`
}

export type AssistantTextSegment =
    | { kind: 'text'; text: string }
    | { kind: 'link'; label: string; href: string };

/** True for safe link targets — only http and https. Rejects
 *  javascript:, data:, mailto:, tel:, file:, vbscript:, etc.
 *  The MD_LINK_RE already enforces http(s) in regex form; this
 *  helper is the secondary check used by `parseAssistantTextSegments`
 *  for defense-in-depth if the regex ever loosens. Pure. */
function isSafeHttpUrl(url: string): boolean {
    if (!url || typeof url !== 'string') return false;
    return /^https?:\/\/[^\s<>"']+$/i.test(url.trim());
}

/** Parse assistant text into a sequence of text/link segments.
 *  Pure / no I/O / no React. */
export function parseAssistantTextSegments(text: string): AssistantTextSegment[] {
    if (!text || typeof text !== 'string') return [];
    // Normalize other markdown artifacts first (bold/italic/code/
    // headings) so the segments carry already-cleaned plain text.
    // The link regex still runs against the source to preserve
    // labels with formatting inside them (rare but harmless).
    const segments: AssistantTextSegment[] = [];
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    // QCARE-MD-LOOP-FIX (2026-05-12): use a LOCAL `g`-flag regex instance
    // instead of the module-level `MD_LINK_RE`. The loop body calls
    // `stripMarkdown(...)` which uses `text.replace(MD_LINK_RE, '$1')`;
    // per ECMAScript §22.1.3.18, `String.prototype.replace` with a
    // global regex resets the regex's `lastIndex` to 0 on return. With
    // the shared `MD_LINK_RE` that meant the very next `.exec(text)`
    // restarted from position 0 and rematched the same first link —
    // INFINITE LOOP for any text containing two or more inline links
    // with prose between them. This pinned the JS main thread; iOS
    // Safari's per-tab watchdog killed the page and showed "A problem
    // repeatedly occurred". Trigger payload in PROD: branch reply
    // "وين فرع التحلية؟" → 3 maps.app.goo.gl markdown links. Fix:
    // a local `re = new RegExp(MD_LINK_RE.source, 'g')` is immune to
    // any `lastIndex` mutations from inside the loop body.
    const re = new RegExp(MD_LINK_RE.source, 'g');
    while ((m = re.exec(text)) !== null) {
        const matchStart = m.index;
        const matchEnd = re.lastIndex;
        const label = m[1];
        const href = m[2];
        if (matchStart > lastIdx) {
            const before = stripMarkdown(text.slice(lastIdx, matchStart));
            if (before) segments.push({ kind: 'text', text: before });
        }
        if (isSafeHttpUrl(href)) {
            segments.push({ kind: 'link', label: stripMarkdown(label), href });
        } else {
            // Unsafe URL — render as plain text, preserving the
            // raw markdown so reviewers can spot it. Test 3 below
            // pins this fallback.
            segments.push({ kind: 'text', text: stripMarkdown(m[0]) });
        }
        lastIdx = matchEnd;
    }
    if (lastIdx < text.length) {
        const tail = stripMarkdown(text.slice(lastIdx));
        if (tail) segments.push({ kind: 'text', text: tail });
    }
    return segments;
}

/** Render assistant text with safe inline Markdown link support.
 *  Returns a React node (string for link-free text, fragment array
 *  with `<a>` anchors when links are present). Pure render — no
 *  state, no effects. */
export function renderAssistantText(text: string): React.ReactNode {
    if (!text) return null;
    const segments = parseAssistantTextSegments(text);
    // Fast-path: no links → return the stripped string directly so
    // existing whitespace-pre-line CSS behavior is preserved.
    if (segments.every(s => s.kind === 'text')) {
        return segments.map(s => (s as { text: string }).text).join('');
    }
    return (
        <>
            {segments.map((s, i) => {
                if (s.kind === 'text') {
                    return <React.Fragment key={i}>{s.text}</React.Fragment>;
                }
                return (
                    <a
                        key={i}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#0ea5b8',
                            textDecoration: 'underline',
                            fontWeight: 600,
                            // Preserves RTL flow; iOS Safari sometimes
                            // strips inheritance on inline <a> children.
                            direction: 'inherit',
                            unicodeBidi: 'isolate',
                        }}
                    >
                        {s.label}
                    </a>
                );
            })}
        </>
    );
}
